// frontend/src/components/fleet/FleetVehicleDetail.jsx
//
// Detalle que se despliega al abrir un vehículo en la vista lista.
//
// Diseñado para ser DENSO: el detalle vive dentro de una lista, así que cada
// bloque compite por altura con las demás unidades. El plan de acción va como
// mosaico de posiciones (no como tabla vertical) y el mapa solo se dibuja
// cuando la vista no tiene ya un mapa al lado.
//
// Todos los números vienen calculados del backend; aquí solo se formatean con
// las unidades y decimales del usuario.

import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';

import { api } from '../../services/api';
import { useUnitPreferences } from '../../hooks/useUnitPreferences';
import {
  formatPressureValue,
  formatTemperatureValue,
  formatSpeedValue,
  formatDistanceValue,
  formatHoursValue,
} from '../../utils/unitFormat';
import FleetMapPanel from './FleetMapPanel';

const SEVERITY_COLORS = { alarm: 'warning', critical: 'error', extreme: 'error' };

// Color del mosaico por acción requerida (mismo criterio que el modo frío del
// esquemático: el color comunica el ajuste, no la alarma).
const PLAN_TONE = {
  ok: { fg: 'success.dark', bg: 'success.50', border: 'success.light' },
  subir: { fg: 'warning.dark', bg: 'warning.50', border: 'warning.light' },
  bajar: { fg: 'warning.dark', bg: 'warning.50', border: 'warning.light' },
  sin_reporte: { fg: 'text.secondary', bg: 'grey.100', border: 'grey.300' },
  sin_sensor: { fg: 'text.disabled', bg: 'grey.50', border: 'grey.200' },
};

/** Métrica compacta en una línea: "Velocidad 27 km/h". */
const InlineStat = ({ label, value }) => (
  <Stack direction="row" spacing={0.5} alignItems="baseline">
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={700}>{value}</Typography>
  </Stack>
);

InlineStat.propTypes = { label: PropTypes.string, value: PropTypes.node };

/**
 * Mosaico del plan de acción: una celda por posición con el ajuste ya calculado
 * por el backend. Sustituye a la tabla vertical de 6 columnas — misma
 * información en una fracción del alto.
 */
const PlanMosaic = ({ positions, preferences, focusPosition, onFocusPosition }) => {
  const adjustmentText = (p) => {
    if (p.status === 'subir' || p.status === 'bajar') {
      return `${p.status === 'subir' ? '▲' : '▼'} ${formatPressureValue(p.delta, preferences, { showUnit: false })}`;
    }
    if (p.status === 'ok') return '✓ OK';
    if (p.status === 'sin_reporte') return '🛜';
    return '—';
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {positions.map((p) => {
        const tone = PLAN_TONE[p.status] || PLAN_TONE.sin_sensor;
        const tip = [
          `Posición ${p.position}${p.axle ? ` · Eje ${p.axle}` : ''}`,
          p.psi !== null && p.psi !== undefined ? `Actual: ${formatPressureValue(p.psi, preferences)}` : null,
          p.temp !== null && p.temp !== undefined ? `Temperatura: ${formatTemperatureValue(p.temp, preferences)}` : null,
          p.psi_cold !== null && p.psi_cold !== undefined ? `Frío estimado: ${formatPressureValue(p.psi_cold, preferences)}` : null,
          p.ideal !== null && p.ideal !== undefined ? `Ideal frío: ${formatPressureValue(p.ideal, preferences)}` : null,
          p.summary || null,
          p.stale && p.age_label ? `Lectura de hace ${p.age_label}` : null,
        ].filter(Boolean).join('\n');
        const focused = focusPosition === p.position;

        return (
          <Tooltip key={p.position} title={<Box sx={{ whiteSpace: 'pre-line' }}>{tip}</Box>} arrow>
            <Box
              onClick={onFocusPosition ? () => onFocusPosition(p.position) : undefined}
              sx={{
                minWidth: 66,
                px: 0.75,
                py: 0.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: tone.border,
                bgcolor: tone.bg,
                textAlign: 'center',
                position: 'relative',
                cursor: onFocusPosition ? 'pointer' : 'default',
                outline: focused ? '2px solid' : 'none',
                outlineColor: 'primary.main',
                outlineOffset: '1px',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                {p.position}
              </Typography>
              <Typography variant="body2" fontWeight={800} sx={{ color: tone.fg, lineHeight: 1.2 }}>
                {adjustmentText(p)}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.1, fontSize: '0.62rem' }}>
                {p.psi !== null && p.psi !== undefined
                  ? `${formatPressureValue(p.psi, preferences, { showUnit: false })}`
                  : '—'}
                {p.temp !== null && p.temp !== undefined
                  ? ` · ${formatTemperatureValue(p.temp, preferences, { showUnit: false })}°`
                  : ''}
              </Typography>
              {/* Marca discreta de lectura no reciente: no se oculta el dato. */}
              {p.stale && (
                <Box sx={{
                  position: 'absolute', top: 3, right: 3, width: 5, height: 5, borderRadius: '50%',
                  bgcolor: p.comm_status === 'no_report' ? 'grey.700' : 'grey.400',
                }} />
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

PlanMosaic.propTypes = {
  positions: PropTypes.array,
  preferences: PropTypes.object,
  focusPosition: PropTypes.number,
  onFocusPosition: PropTypes.func,
};

const FleetVehicleDetail = ({
  vehicleId, windowHours = 24, showMap = true,
  // Posicion enfocada: filtra la grafica. La controla la lista (al pulsar una
  // columna) y tambien el mosaico del plan de accion.
  focusPosition = null, onFocusPosition = null,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { preferences } = useUnitPreferences();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartMetric, setChartMetric] = useState('pressure');

  useEffect(() => {
    if (!vehicleId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.fleet.getVehicleDetail(vehicleId, { window_hours: windowHours })
      .then((res) => { if (!cancelled) setDetail(res.data); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail || 'No se pudo cargar el detalle del vehículo');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [vehicleId, windowHours]);

  // Series a formato recharts: una línea por posición, muestreadas para no
  // ahogar el render en flotas con mucha telemetría.
  const chartData = useMemo(() => {
    if (!detail?.series) return { rows: [], positions: [] };
    const positions = Object.keys(detail.series).sort((a, b) => Number(a) - Number(b));
    const byTime = new Map();
    positions.forEach((position) => {
      (detail.series[position] || []).forEach((point) => {
        if (!point.at) return;
        const row = byTime.get(point.at) || { at: point.at };
        row[`p${position}`] = point.pressure;
        row[`t${position}`] = point.temperature;
        byTime.set(point.at, row);
      });
    });
    let rows = Array.from(byTime.values()).sort((a, b) => new Date(a.at) - new Date(b.at));
    const MAX_POINTS = 200;
    if (rows.length > MAX_POINTS) {
      const step = Math.ceil(rows.length / MAX_POINTS);
      rows = rows.filter((_, index) => index % step === 0);
    }
    return { rows, positions };
  }, [detail]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (error) return <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>;
  if (!detail) return null;

  const gps = detail.gps || {};
  const positions = detail.positions || [];
  const tickets = detail.tickets || [];
  const ideals = Object.entries(detail.ideal_by_axle || {});
  const palette = [
    theme.palette.primary.main, theme.palette.error.main, theme.palette.success.main,
    theme.palette.warning.main, theme.palette.info.main, theme.palette.secondary.main,
  ];
  const prefix = chartMetric === 'pressure' ? 'p' : 't';
  // Al enfocar una posicion la grafica muestra SOLO esa serie; sin foco, todas.
  const chartPositions = focusPosition
    ? chartData.positions.filter((position) => Number(position) === Number(focusPosition))
    : chartData.positions;

  const timeLabel = (value) => {
    try {
      return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (_) {
      return value;
    }
  };

  const content = (
    <Stack spacing={1.25}>
      {/* Resumen operativo en una sola línea */}
      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
        <InlineStat label={t('hardcoded.components_fleet.velocidad')} value={formatSpeedValue(gps.speed_kmh, preferences)} />
        <InlineStat label={t('hardcoded.components_fleet.distancia')} value={formatDistanceValue(gps.distance_km, preferences)} />
        <InlineStat label={t('hardcoded.components_fleet.tiempo')} value={formatHoursValue(gps.moving_hours)} />
        <InlineStat label={t('hardcoded.components_fleet.tickets_activos')} value={tickets.length} />
      </Stack>

      {/* Plan de acción compacto */}
      {positions.length > 0 && (
        <Box>
          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap', mb: 0.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
              {t('hardcoded.components_fleet.plan_de_accion')}
            </Typography>
            {ideals.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Ideal en frío — {ideals.map(([axle, value]) => `Eje ${axle}: ${formatPressureValue(value, preferences)}`).join(' · ')}
              </Typography>
            )}
          </Stack>
          <PlanMosaic
            positions={positions}
            preferences={preferences}
            focusPosition={focusPosition}
            onFocusPosition={onFocusPosition}
          />
          {detail.cold_note && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.68rem' }}>
              {detail.cold_note}
            </Typography>
          )}
        </Box>
      )}

      {/* Histórico: una gráfica con conmutador, no dos bloques apilados */}
      {chartData.rows.length > 1 && (
        <Box>
          <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
              {t('hardcoded.components_fleet.historico_reciente')}
            </Typography>
            {focusPosition ? (
              <Chip
                size="small"
                color="primary"
                label={`Pos. ${focusPosition}`}
                onDelete={onFocusPosition ? () => onFocusPosition(focusPosition) : undefined}
                sx={{ ml: 1, height: 20, fontSize: '0.68rem' }}
              />
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Todas las posiciones
              </Typography>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={chartMetric}
              onChange={(event, next) => next && setChartMetric(next)}
              sx={{ '& .MuiToggleButton-root': { py: 0.1, px: 1, fontSize: '0.68rem', textTransform: 'none' } }}
            >
              <ToggleButton value="pressure">{t('hardcoded.components_fleet.presion')}</ToggleButton>
              <ToggleButton value="temperature">{t('hardcoded.components_fleet.temperatura')}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Paper variant="outlined" sx={{ p: 0.5 }}>
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={chartData.rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="at" tickFormatter={timeLabel} fontSize={10} minTickGap={44} />
                <YAxis fontSize={10} width={34} />
                <RechartsTooltip labelFormatter={timeLabel} contentStyle={{ fontSize: 11 }} />
                {chartPositions.map((position, index) => (
                  <Line
                    key={position}
                    type="monotone"
                    dataKey={`${prefix}${position}`}
                    name={`Pos. ${position}`}
                    stroke={palette[index % palette.length]}
                    dot={false}
                    strokeWidth={1.4}
                    connectNulls
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Box>
      )}

      {/* Tickets activos */}
      {tickets.length > 0 && (
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
            {t('hardcoded.components_fleet.tickets_activos')}
          </Typography>
          <Stack spacing={0.4}>
            {tickets.map((ticket) => (
              <Stack key={ticket.ticket_id} direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  color={SEVERITY_COLORS[ticket.severity] || 'default'}
                  label={ticket.ticket_number || ticket.ticket_id.slice(0, 8)}
                  sx={{ height: 20, fontSize: '0.68rem' }}
                />
                <Typography variant="caption" fontWeight={600}>
                  {ticket.metric_type}
                  {ticket.position ? ` · Eje ${ticket.axle} · Pos. ${ticket.position}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {ticket.reason_text}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );

  // El mapa del detalle solo aparece cuando la vista NO trae ya un mapa al lado
  // (en la vista dividida sería el mismo mapa dos veces).
  if (!showMap) return <Box sx={{ py: 1 }}>{content}</Box>;

  return (
    <Box sx={{ py: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>{content}</Grid>
        <Grid item xs={12} md={5}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
            {t('hardcoded.components_fleet.ubicacion')}
          </Typography>
          <Divider sx={{ mb: 0.75 }} />
          <FleetMapPanel
            height={300}
            markers={gps.has_location ? [{
              vehicle_id: detail.vehicle.id,
              vehicle_name: detail.vehicle.name,
              latitude: gps.latitude,
              longitude: gps.longitude,
              speed_kmh: gps.speed_kmh,
              open_tickets: tickets.length,
              status: tickets.length ? 'alarma' : 'ok',
            }] : []}
            selectedVehicleId={detail.vehicle.id}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

FleetVehicleDetail.propTypes = {
  vehicleId: PropTypes.string.isRequired,
  windowHours: PropTypes.number,
  showMap: PropTypes.bool,
};

export default FleetVehicleDetail;
